import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Form } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import pharmacyService from '../../../services/pharmacyService';
import { usePharmacy } from '../pharmacyContext';
import { PHARMACY_COPY, severityLabel, statusLabel } from '../pharmacyCopy';
import Icon from '../../common/Icons';
import { PharmacyPageHeader, LoadingBlock, EmptyBlock, formatNumber, formatDate } from '../ui';
import DispensationOriginBadge from '../components/DispensationOriginBadge';
import PrescriptionDocumentSheet, {
  dispensationToPrescriptionView
} from '../components/PrescriptionDocumentSheet';
import SmartDispensationComposer from '../components/SmartDispensationComposer';
import {
  DispensationFlow,
  dispensationStatusClass,
  nextDispenseActionLabel,
  severityPillClass,
  useDispensationRoutes
} from '../components/dispensationUi';
import { printPrescriptionA4 } from '../../doctor/utils/prescriptionA4Print';
import {
  formatPharmacyLineTotal,
  formatPharmacyPrice,
  summarizeDispensationPricing
} from '../utils/dispensationPricing';

const copy = PHARMACY_COPY.DISPENSATIONS;

function stockTone(it) {
  const qty = Number(it.quantity || 0);
  const onHand = Number(it.on_hand ?? 0);
  if (it.stock_status === 'OUT_OF_STOCK' || onHand <= 0) return 'out';
  if (!onHand || qty > onHand) return 'low';
  return 'ok';
}

function detailToForm(detail) {
  return {
    patient_name: detail.patient_name || '',
    notes: detail.prescription_notes || detail.notes || '',
    items: (detail.items || []).map((it) => ({
      medication_id: String(it.medication_id),
      quantity: String(it.quantity),
      dosage_instructions: it.dosage_instructions || ''
    }))
  };
}

export default function PharmacyDispensationDetailPage({ embedded = false, dispensationIdProp, onBack }) {
  const { dispensationId: routeId } = useParams();
  const dispensationId = dispensationIdProp || routeId;
  const navigate = useNavigate();
  const routes = useDispensationRoutes();
  const { toast, bumpReload } = usePharmacy();

  const [detail, setDetail] = useState(null);
  const [medications, setMedications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [editInteractionPreview, setEditInteractionPreview] = useState(null);
  const [overrideReason, setOverrideReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await pharmacyService.getDispensation(dispensationId);
      if (res.success) {
        setDetail(res.data);
        setOverrideReason('');
      } else {
        setDetail(null);
        toast('danger', res.message || 'Dispensation introuvable.');
      }
    } catch (e) {
      setDetail(null);
      toast('danger', e.message || 'Dispensation introuvable.');
    } finally {
      setLoading(false);
    }
  }, [dispensationId, toast]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const medRes = await pharmacyService.listMedications({ status: 'ACTIVE' });
        if (!cancelled) setMedications(medRes.success ? medRes.data || [] : []);
      } catch {
        if (!cancelled) setMedications([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!editing || !editForm) {
      setEditInteractionPreview(null);
      return undefined;
    }
    const ids = editForm.items.map((l) => l.medication_id).filter(Boolean);
    if (ids.length < 2) {
      setEditInteractionPreview(null);
      return undefined;
    }
    let cancelled = false;
    pharmacyService.checkInteractions(ids).then((res) => {
      if (!cancelled && res.success) setEditInteractionPreview(res.data);
    });
    return () => {
      cancelled = true;
    };
  }, [editForm, editing]);

  const goBack = () => {
    if (embedded && onBack) onBack();
    else navigate(routes.LIST);
  };

  const act = async (fn, okMsg, { refreshOnly = false } = {}) => {
    setBusy(true);
    try {
      const res = await fn();
      if (res.success) {
        toast('success', okMsg);
        bumpReload?.();
        if (refreshOnly && res.data) {
          setDetail(res.data);
          setOverrideReason('');
        } else if (embedded && onBack) onBack();
        else if (res.data) {
          setDetail(res.data);
          setOverrideReason('');
        } else goBack();
      } else {
        toast('danger', res.message || 'Échec.');
      }
    } catch (e) {
      toast('danger', e.message || 'Échec.');
    } finally {
      setBusy(false);
    }
  };

  const startEdit = () => {
    setEditForm(detailToForm(detail));
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setEditForm(null);
    setEditInteractionPreview(null);
  };

  const editLineCount = useMemo(() => {
    if (!editForm) return 0;
    return editForm.items.filter((l) => l.medication_id && l.quantity).length;
  }, [editForm]);

  const canSaveEdit =
    editing &&
    editForm &&
    editLineCount > 0 &&
    editForm.patient_name.trim().length >= 2 &&
    !busy;

  const saveEdit = async () => {
    if (!canSaveEdit) return;
    const items = editForm.items
      .filter((l) => l.medication_id && l.quantity)
      .map((l) => ({
        medication_id: l.medication_id,
        quantity: l.quantity,
        dosage_instructions: l.dosage_instructions || null
      }));

    setBusy(true);
    try {
      const res = await pharmacyService.updateDispensation(detail.id, {
        site_id: detail.site_id,
        patient_name: editForm.patient_name.trim(),
        notes: editForm.notes.trim() || null,
        items
      });
      if (res.success) {
        toast('success', copy.EDIT_SAVE_OK);
        bumpReload?.();
        setDetail(res.data);
        setEditing(false);
        setEditForm(null);
        setEditInteractionPreview(null);
        setOverrideReason('');
      } else {
        toast('danger', res.message || 'Échec.');
      }
    } catch (e) {
      toast('danger', e.message || 'Échec.');
    } finally {
      setBusy(false);
    }
  };

  const doDispense = () => {
    const blocking = detail?.interactions?.blocking;
    act(
      () =>
        pharmacyService.dispense(detail.id, {
          override: blocking,
          override_reason: blocking ? overrideReason : undefined
        }),
      copy.ACT_DISPENSE_OK,
      { refreshOnly: true }
    );
  };

  const cancelDispense = async () => {
    await act(() => pharmacyService.cancelDispensation(detail.id), copy.CANCEL_OK);
  };

  if (loading) return <LoadingBlock label="Chargement de la dispensation…" />;
  if (!detail) {
    return (
      <div className="cp-order-page">
        <EmptyBlock label="Dispensation introuvable." />
        <button type="button" className="cp-btn cp-btn--ghost mt-3" onClick={goBack}>
          {copy.BACK_TO_LIST}
        </button>
      </div>
    );
  }

  const blocking = detail.interactions?.blocking;
  const interactions = editing
    ? editInteractionPreview?.interactions || []
    : detail.interactions?.interactions || [];
  const interactionSeverity = editing
    ? editInteractionPreview?.highestSeverity
    : detail.interactions?.highestSeverity;
  const interactionBlocking = editing ? editInteractionPreview?.blocking : blocking;
  const items = [...(detail.items || [])].sort((a, b) => Number(a.id) - Number(b.id));
  const pricing = detail.pricing || summarizeDispensationPricing(items).pricing;
  const { consultation, prescription } = dispensationToPrescriptionView(detail);
  const sheetMode = detail.source_type === 'PRESCRIPTION' ? 'prescription' : 'manual';
  const showLots = detail.status === 'DISPENSED';
  const canEdit = detail.status === 'PENDING';

  const panelItems = editing && editForm
    ? summarizeDispensationPricing(
        editForm.items
          .filter((l) => l.medication_id)
          .map((line) => {
            const med = medications.find((m) => String(m.id) === String(line.medication_id));
            const existing = detail.items.find((it) => String(it.medication_id) === String(line.medication_id));
            return {
              id: existing?.id || line.medication_id,
              medication_id: line.medication_id,
              medication_name: med?.name || existing?.medication_name || `Médicament #${line.medication_id}`,
              quantity: parseInt(line.quantity, 10) || 0,
              dosage_instructions: line.dosage_instructions || '',
              unit_name: med?.unit_name || existing?.unit_name || '',
              price: med?.price ?? existing?.price ?? null,
              on_hand: existing?.on_hand ?? 0,
              stock_status: existing?.stock_status,
              lot_number: existing?.lot_number,
              expiry_date: existing?.expiry_date,
              lot_is_preview: existing?.lot_is_preview
            };
          })
      ).items
    : items;

  const panelPricing =
    editing && editForm ? summarizeDispensationPricing(panelItems).pricing : pricing;

  return (
    <div className={`cp-order-page cp-dispense-detail${embedded ? ' cp-order-page--embedded' : ''}`}>
      <PharmacyPageHeader
        badge={copy.BADGE}
        title={detail.dispensation_number}
        description={
          <span className="d-inline-flex flex-wrap align-items-center gap-2">
            <DispensationOriginBadge sourceType={detail.source_type} />
            <span className={`cp-status-pill ${dispensationStatusClass(detail.status)}`}>
              {statusLabel(detail.status)}
            </span>
            <span className="text-muted">{nextDispenseActionLabel(detail.status)}</span>
            {editing ? (
              <span className="cp-status-pill cp-status-pill--warn">{copy.EDIT_MODE_TITLE}</span>
            ) : null}
          </span>
        }
        actions={
          <div className="d-flex flex-wrap gap-2">
            {canEdit && !editing ? (
              <button type="button" className="cp-btn cp-btn--ghost" onClick={startEdit}>
                <Icon name="edit" size={16} aria-hidden />
                {copy.EDIT_SHEET}
              </button>
            ) : null}
            {!editing ? (
              <button type="button" className="cp-btn cp-btn--ghost" onClick={() => printPrescriptionA4()}>
                {copy.PRINT_SHEET}
              </button>
            ) : null}
            <button type="button" className="cp-btn cp-btn--ghost" onClick={goBack}>
              <Icon name="chevronLeft" size={16} aria-hidden />
              {copy.BACK_TO_LIST}
            </button>
          </div>
        }
      />

      <div className="cp-dispense-detail-layout">
        <section className="cp-dispense-detail-layout__doc" aria-label={copy.SHEET_ARIA}>
          {editing && editForm ? (
            <div className="cp-dispense-edit-wrap">
              {detail.source_type === 'PRESCRIPTION' ? (
                <Alert variant="info" className="cp-dispense-edit-hint py-2 small mb-3">
                  {copy.EDIT_RX_HINT}
                </Alert>
              ) : null}
              <SmartDispensationComposer
                medications={medications}
                siteId={detail.site_id}
                siteLabel={detail.site_name || ''}
                value={editForm}
                onChange={setEditForm}
                copy={copy}
                interactionPreview={editInteractionPreview}
              />
              <div className="cp-dispense-edit-actions">
                <button type="button" className="cp-btn cp-btn--ghost" disabled={busy} onClick={cancelEdit}>
                  {copy.EDIT_CANCEL}
                </button>
                <button type="button" className="cp-btn cp-btn--primary" disabled={!canSaveEdit} onClick={saveEdit}>
                  {busy ? copy.SAVING : copy.EDIT_SAVE}
                </button>
              </div>
            </div>
          ) : (
            <div className="cp-rx-sheet-wrap">
            <PrescriptionDocumentSheet
              consultation={consultation}
              prescription={prescription}
              mode={sheetMode}
              showLots={showLots}
              showPricing
              pricing={pricing}
              totalPriceLabel={copy.TOTAL_PRICE}
              partialPriceHint={copy.PRICE_PARTIAL_HINT}
            />
            </div>
          )}
        </section>

        <aside className="cp-dispense-detail-layout__panel">
          <DispensationFlow status={detail.status} />

          {interactions.length > 0 ? (
            <div
              className={`cp-interaction-panel${interactionBlocking ? ' cp-interaction-panel--danger' : ' cp-interaction-panel--warn'}`}
            >
              <div className="cp-interaction-panel__head">
                <Icon name="shield" size={16} aria-hidden />
                <strong>
                  {copy.INTERACTION_TITLE.replace('{severity}', severityLabel(interactionSeverity))}
                </strong>
                <span className={`cp-status-pill ${severityPillClass(interactionSeverity)}`}>
                  {severityLabel(interactionSeverity)}
                </span>
              </div>
              <ul className="cp-interaction-panel__list mb-0">
                {interactions.map((i) => (
                  <li key={i.id}>
                    <strong>{i.medication_a_name}</strong> + <strong>{i.medication_b_name}</strong>
                    <span className="text-muted"> — {i.description}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="cp-dispense-stock-ok">
              <Icon name="shield" size={16} aria-hidden />
              <span>{copy.INTERACTION_OK_DETAIL}</span>
            </div>
          )}

          <section className="cp-dispense-stock-panel">
            <h3 className="cp-dispense-stock-panel__title">{copy.PANEL_STOCK_TITLE}</h3>
            <p className="cp-dispense-stock-panel__hint">{copy.PANEL_STOCK_HINT}</p>
            <ul className="cp-dispense-stock-panel__list">
              {panelItems.map((it) => {
                const tone = stockTone(it);
                return (
                  <li key={it.id} className={`cp-dispense-stock-panel__item cp-dispense-stock-panel__item--${tone}`}>
                    <div className="cp-dispense-stock-panel__med">
                      <strong>{it.medication_name}</strong>
                      <span className="text-muted">
                        {formatNumber(it.quantity)} demandé
                        {it.dosage_instructions ? ` · ${it.dosage_instructions}` : ''}
                      </span>
                    </div>
                    <div className="cp-dispense-stock-panel__stock">
                      <span className={`cp-dispense-stock cp-dispense-stock--${tone}`}>
                        {formatNumber(it.on_hand ?? 0)} {it.unit_name || ''}
                      </span>
                      <span className="cp-dispense-stock-panel__price">
                        {formatPharmacyPrice(it.price) || copy.NO_PRICE}
                        {it.line_total != null ? ` · ${formatPharmacyLineTotal(it.line_total)}` : ''}
                      </span>
                      {it.lot_number || it.expiry_date ? (
                        <span className="cp-dispense-stock-panel__lot">
                          {it.lot_number ? (
                            <strong>
                              {it.lot_is_preview ? 'Lot FEFO ' : 'Lot '}
                              {it.lot_number}
                            </strong>
                          ) : (
                            <strong>Lot —</strong>
                          )}
                          {it.expiry_date ? (
                            <span className="cp-dispense-stock-panel__expiry">
                              {' '}
                              · Péremption {formatDate(it.expiry_date)}
                            </span>
                          ) : null}
                        </span>
                      ) : (
                        <span className="cp-dispense-stock-panel__lot cp-dispense-stock-panel__lot--empty">
                          Lot / péremption : —
                        </span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
            {panelPricing?.total > 0 ? (
              <div className="cp-dispense-stock-panel__total">
                <span>{copy.TOTAL_PRICE}</span>
                <strong>{formatPharmacyLineTotal(panelPricing.total)}</strong>
              </div>
            ) : null}
            {panelPricing && !panelPricing.complete && panelItems.length > 0 ? (
              <p className="cp-dispense-stock-panel__price-hint mb-0">{copy.PRICE_PARTIAL_HINT}</p>
            ) : null}
          </section>

          {detail.status === 'PENDING' && !editing ? (
            <>
              {blocking ? (
                <section className="cp-form-section cp-form-section--override">
                  <Form.Group>
                    <Form.Label className="text-danger fw-semibold">{copy.OVERRIDE_LABEL}</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={overrideReason}
                      onChange={(e) => setOverrideReason(e.target.value)}
                      placeholder={copy.OVERRIDE_PLACEHOLDER}
                    />
                    <Form.Text>{copy.OVERRIDE_MIN}</Form.Text>
                  </Form.Group>
                </section>
              ) : null}

              <div className="cp-order-page__actions cp-order-page__actions--panel">
                <div className="cp-order-page__actions-meta">
                  <span className="text-muted">{copy.ACTIONS_HINT}</span>
                </div>
                <div className="cp-order-page__actions-btns cp-order-page__actions-btns--stack">
                  <button
                    type="button"
                    className="cp-btn cp-btn--primary"
                    disabled={busy || (blocking && overrideReason.trim().length < 5)}
                    onClick={doDispense}
                  >
                    {blocking ? copy.ACT_DISPENSE_OVERRIDE : copy.ACT_DISPENSE}
                  </button>
                  <button type="button" className="cp-btn cp-btn--danger" disabled={busy} onClick={cancelDispense}>
                    {copy.ACT_CANCEL}
                  </button>
                </div>
              </div>
            </>
          ) : null}
        </aside>
      </div>
    </div>
  );
}
